import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { tourFileAPI, seriesTemplateAPI, tourLeaderAPI, transportProviderAPI } from '../services/api';
import { Trash2 } from 'lucide-react';
import { User } from '../App';

// Code lookup tables based on user's reference table
const CITY_CODES: {[key: string]: string} = {
  'Delhi': 'DL',
  'Bhubaneswar': 'BB',
  'Cochin': 'CK'
};

const FILE_MANAGER_CODES: {[key: string]: string} = {
  'Anima Biswal': 'AB',
  'Madhu Chaudhary': 'MC',
  'Sumesh Sudharasan': 'SS',
  'Pamlesh Rana': 'PR',
  'Ujjwal': 'UK',
  'Shambu Rawat': 'SR',
  'Jitendra Yadav': 'JY',
  'Nidhi Gopal': 'NG',
  'Ketan Gupta': 'KG',
  'Sunil Verma': 'SV'
};

const AGENT_CODES: {[key: string]: string} = {
  'Venus Travel': 'VT',
  'Viva Holidays': 'VH',
  'Inspiring Vacations': 'IV',
  'Sanskriti Tours': 'ST',
  'Soulful Safaris': 'SS',
  'Direct Client': 'DC',
  'Referral Client': 'RF',
  '24x7 Rooms com': 'RC',
  'Trip Tailor': 'TT',
  'Muziris Heritage': 'MH',
  'Explore': 'EX',
  'Fest Travel': 'FT',
  'Japan': 'JP',
  'ROCS': 'RO',
  'Buckenham Travel': 'BT',
  'Celebration India Holidays': 'CI',
  'Hooting Owls': 'HO',
  'Royal Mountain Tours': 'RM',
  'Flywell Tours': 'FW',
  'Destinations World wide': 'DW',
  'Business Promotion / FAM': 'BP'
};

// Helper function to extract base pattern from file code (excluding month/year/sequence)
// Example: DLMC0127EX0001 -> DLMCEX (city+manager+operator)
function getFileCodeBasePattern(fileCode: string): string | null {
  if (!fileCode || fileCode.length < 10) return null;
  try {
    // Format: CCMMMMYYYYOO#### where:
    // CC = 2 char city, MMMM = 2-4 char manager, YYYY = 2-4 char year, OO = 2 char operator, #### = 4 digit sequence
    // We want: CC + MMMM + OO (skip month/year and sequence)
    // Pattern: First 2-6 chars + skip 4 chars (month+year) + next 2 chars
    const match = fileCode.match(/^([A-Z]{2})([A-Z]{2})(\d{2})(\d{2})([A-Z]{2})\d{4}$/);
    if (match) {
      return match[1] + match[2] + match[5]; // City + Manager + Operator
    }
    return null;
  } catch (e) {
    return null;
  }
}

interface TourFile {
  id: string;
  fileCode: string;
  tourName: string;
  status?: string;
  [key: string]: any;
}

interface TourFileCoverProps {
  tour: TourFile;
  canEdit: boolean;
  onUpdate?: (updatedTour: TourFile) => void;
  onEditingChange?: (isEditing: boolean) => void;
  onPaxChange?: (newPax: number) => void;
  extensions?: Array<{
    id: string;
    passengerName: string;
    position?: 'pre' | 'post';
    date: string;
    sector: string;
    flightNumber: string;
    time: string;
    status: string;
    guideName?: string;
    guideType?: string;
    city: string;
    hotelName: string;
    hotelStatus: string;
    roomCategory: string;
    meals: string[];
    breakfastRemark?: string;
    lunchRemark?: string;
    dinnerRemark?: string;
  }>;
  passengerDetails?: Array<{
    id: string;
    roomType: string;
    name: string;
    dob: string;
    age: string;
    sex: string;
    nationality: string;
    passportNo: string;
    issueDate: string;
    expiryDate: string;
    placeOfIssue: string;
  }>;
  seriesFields?: {
    fileName: string;
    country: string;
    city: string;
    fileManager: string;
    year: number;
    month: number;
    fileTourOperator: string;
  };
  currentUser?: User;
}

// Helper function to get day of week from date string (dd-mmm-yy format)
const getDayOfWeek = (dateStr: string): string => {
  if (!dateStr || dateStr.trim() === '') return '';
  
  try {
    const parts = dateStr.trim().split('-');
    if (parts.length !== 3) return '';
    
    const day = parseInt(parts[0]);
    const monthStr = parts[1];
    const year = parseInt('20' + parts[2]);
    
    const monthMap: {[key: string]: number} = {
      'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3,
      'May': 4, 'Jun': 5, 'Jul': 6, 'Aug': 7,
      'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
    };
    
    const month = monthMap[monthStr];
    if (month === undefined || isNaN(day) || isNaN(year)) return '';
    
    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) return '';
    
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return dayNames[date.getDay()];
  } catch (error) {
    return '';
  }
};

export function TourFileCover({ tour, canEdit, onUpdate, onEditingChange, onPaxChange, extensions = [], passengerDetails = [], seriesFields, currentUser }: TourFileCoverProps) {
  // Auto-enable editing for new tours, otherwise start in view mode
  const [isEditing, setIsEditing] = useState(tour.id?.startsWith('new-') || false);
  
  // Guard to prevent auto-save before initial data load completes
  const dataLoadedRef = useRef(false);
  
  // Notify parent when editing state changes
  useEffect(() => {
    onEditingChange?.(isEditing);
  }, [isEditing, onEditingChange]);
  
  // Safely parse arrays from tour data
  const parseArray = (value: any): any[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const [editedTour, setEditedTour] = useState<TourFile>(tour);
  const [checklistItems, setChecklistItems] = useState({
    hotels: '',
    voucher: '',
    trainTickets: '',
    airTicket: '',
    trainTkt: '',
    safaris: '',
    trainTicket120: '',
    specials: '',
    flight: '',
    flightTime: '',
    provider: '',
    transport: ''
  });

  // State for checkbox grids (4 columns each for checklist items)
  const [checkboxState, setCheckboxState] = useState<{[key: string]: boolean[]}>({
    hotels: [false, false, false, false],
    voucher: [false, false, false, false],
    trainTickets: [false, false, false, false],
    airTicket: [false, false, false, false],
    trainTkt: [false, false, false, false],
    safaris: [false, false, false, false],
    trainTicket120: [false, false, false, false],
    specials: [false, false, false, false]
  });

  // State for multiple tour leaders (max 2)
  const [tourLeaders, setTourLeaders] = useState<Array<{id: string, name: string, type: 'Half Day' | 'Full Day'}>>([]);
  // State for guides (renamed from old tour leaders)
  const [guides, setGuides] = useState<Array<{id: string, name: string, type: 'Half Day' | 'Full Day'}>>([]);

  // State for multiple transports
  const [transports, setTransports] = useState<Array<{id: string, type: string, startDate: string, endDate: string, provider: string}>>([]);
  const [transportProviders, setTransportProviders] = useState<string[]>([]);
  const [allTransportProviders, setAllTransportProviders] = useState<string[]>([]);
  const [availableTourLeaders, setAvailableTourLeaders] = useState<Array<{id: string; name: string}>>([]);

  // Load all transport providers from database
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const res = await transportProviderAPI.getAll();
        if (res.success) {
          const names = res.data.map((p: any) => p.name);
          setAllTransportProviders(names);
        }
      } catch (err) { console.error('Failed to load transport providers', err); }
    };
    fetchProviders();
  }, []);

  // Load tour leaders for dropdown
  useEffect(() => {
    const fetchLeaders = async () => {
      try {
        const res = await tourLeaderAPI.getAll();
        if (res.success) setAvailableTourLeaders(res.data);
      } catch (err) { console.error('Failed to load tour leaders', err); }
    };
    fetchLeaders();
  }, []);

  // State for itinerary rows (start with 3 rows)
  interface ItineraryRow {
    date: string;
    sector: string;
    flightNumber: string;
    time: string;
    status: string;
    guideName: string; // Guide name
    guideType: string; // 'F.D' or 'H.D'
    city: string;
    hotelName: string;
    hotelStatus: string;
    roomCategory: string;
    meals: string[]; // ['Breakfast'], ['Breakfast', 'Lunch'], ['Breakfast', 'Lunch', 'Dinner']
    breakfastRemark?: string; // Optional remark for breakfast
    lunchRemark?: string; // Optional remark for lunch
    dinnerRemark?: string; // Optional remark for dinner
  }

  const emptyItineraryRow: ItineraryRow = {
    date: '',
    sector: '',
    flightNumber: '',
    time: '',
    status: '',
    guideName: '',
    guideType: '', // Default to blank - user selects F.D or H.D
    city: '',
    hotelName: '',
    hotelStatus: '',
    roomCategory: '',
    meals: ['Breakfast'], // Default to Breakfast
    breakfastRemark: '',
    lunchRemark: '',
    dinnerRemark: ''
  };

  const [itineraryRows, setItineraryRows] = useState<ItineraryRow[]>(
    Array(3).fill(null).map(() => ({ ...emptyItineraryRow }))
  );

  // Update editedTour when tour prop changes (when loading from database)
  useEffect(() => {
    console.log('🔄 Tour data received:', tour.id, tour.tourName);
    
    const parsedHotels = parseArray(tour.hotels);
    const parsedCities = parseArray(tour.cities);
    const parsedGuides = parseArray(tour.guides);
    const parsedItinerary = parseArray(tour.itinerary);
    
    setEditedTour({
      ...tour,
      hotels: parsedHotels,
      cities: parsedCities,
      guides: parsedGuides,
      itinerary: parsedItinerary
    });
    
    setChecklistItems({
      hotels: parsedHotels.join('\n'),
      voucher: tour.voucher || '',
      trainTickets: tour.trainTickets || '',
      airTicket: tour.airTicket || '',
      trainTkt: tour.trainTkt || '',
      safaris: tour.safaris || '',
      trainTicket120: tour.trainTicket120 || '',
      specials: tour.specials || '',
      flight: tour.flight || '',
      flightTime: tour.flightTime || '',
      provider: tour.provider || '',
      transport: tour.transport || ''
    });

    // Load checkbox states from tour data
    if (tour.checkboxStates) {
      try {
        const loadedCheckboxStates = typeof tour.checkboxStates === 'string' 
          ? JSON.parse(tour.checkboxStates) 
          : tour.checkboxStates;
        console.log('📥 Loading checkbox states:', loadedCheckboxStates);
        setCheckboxState({
          hotels: loadedCheckboxStates.hotels || [false, false, false, false],
          voucher: loadedCheckboxStates.voucher || [false, false, false, false],
          trainTickets: loadedCheckboxStates.trainTickets || [false, false, false, false],
          airTicket: loadedCheckboxStates.airTicket || [false, false, false, false],
          trainTkt: loadedCheckboxStates.trainTkt || [false, false, false, false],
          safaris: loadedCheckboxStates.safaris || [false, false, false, false],
          trainTicket120: loadedCheckboxStates.trainTicket120 || [false, false, false, false],
          specials: loadedCheckboxStates.specials || [false, false, false, false]
        });
      } catch (e) {
        console.error('Failed to parse checkbox states:', e);
      }
    }

    // Load itinerary data
    if (parsedItinerary && parsedItinerary.length > 0) {
      const loadedRows = parsedItinerary.map((row: any) => ({
        date: row.date || '',
        sector: row.sector || '',
        flightNumber: row.flightNumber || '',
        time: row.time || '',
        status: row.status || '',
        guideName: row.guideName || '',
        guideType: row.guideType || '',
        city: row.city || '',
        hotelName: row.hotelName || '',
        hotelStatus: row.hotelStatus || '',
        roomCategory: row.roomCategory || '',
        meals: row.meals || (row.roomTicked ? ['Breakfast'] : ['Breakfast']), // Migration: default to Breakfast
        breakfastRemark: row.breakfastRemark || '',
        lunchRemark: row.lunchRemark || '',
        dinnerRemark: row.dinnerRemark || ''
      }));
      
      // Keep at least 3 rows
      while (loadedRows.length < 3) {
        loadedRows.push({
          date: '',
          sector: '',
          flightNumber: '',
          time: '',
          status: '',
          guideName: '',
          guideType: '',
          city: '',
          hotelName: '',
          hotelStatus: '',
          roomCategory: '',
          meals: ['Breakfast'],
          breakfastRemark: '',
          lunchRemark: '',
          dinnerRemark: ''
        });
      }
      
      setItineraryRows(loadedRows);
    } else {
      // Reset to 3 empty rows if no itinerary data
      setItineraryRows(Array(3).fill(null).map(() => ({
        guideName: '',
        guideType: '',
        date: '',
        sector: '',
        flightNumber: '',
        time: '',
        status: '',
        city: '',
        hotelName: '',
        hotelStatus: '',
        roomCategory: '',
        meals: ['Breakfast'],
        breakfastRemark: '',
        lunchRemark: '',
        dinnerRemark: ''
      })));
    }

    // Load tour leaders data (max 2)
    if (tour.tourLeaders) {
      try {
        const parsedLeaders = typeof tour.tourLeaders === 'string' ? JSON.parse(tour.tourLeaders) : tour.tourLeaders;
        if (Array.isArray(parsedLeaders) && parsedLeaders.length > 0) {
          setTourLeaders(parsedLeaders.slice(0, 2)); // Max 2 tour leaders
        }
      } catch (e) {
        console.error('Failed to parse tour leaders:', e);
      }
    }

    // Load guides data (unlimited)
    if (tour.guides) {
      try {
        const parsedGuides = typeof tour.guides === 'string' ? JSON.parse(tour.guides) : tour.guides;
        if (Array.isArray(parsedGuides) && parsedGuides.length > 0) {
          setGuides(parsedGuides);
        }
      } catch (e) {
        console.error('Failed to parse guides:', e);
      }
    } else if (tour.tourLeaders && !tour.guides) {
      // Backward compatibility: if guides field doesn't exist, load from tourLeaders
      try {
        const parsedLeaders = typeof tour.tourLeaders === 'string' ? JSON.parse(tour.tourLeaders) : tour.tourLeaders;
        if (Array.isArray(parsedLeaders) && parsedLeaders.length > 0) {
          setGuides(parsedLeaders);
        }
      } catch (e) {
        console.error('Failed to parse tour leaders (for guides):', e);
      }
    }

    // Load transports data
    if (tour.transports) {
      try {
        const parsedTransports = typeof tour.transports === 'string' ? JSON.parse(tour.transports) : tour.transports;
        if (Array.isArray(parsedTransports) && parsedTransports.length > 0) {
          setTransports(parsedTransports);
        }
      } catch (e) {
        console.error('Failed to parse transports:', e);
      }
    }

    // Check if we receive year and set properly if missing
    
    console.log('✅ Tour data loaded into form');
    dataLoadedRef.current = true;
  }, [tour.id, tour.tourName]); // Re-run when tour ID or name changes

  // Sync editedTour with tour prop changes (for real-time updates from parent)
  useEffect(() => {
    setEditedTour(tour);
    
    // Also reload checkbox states when tour changes
    if (tour.checkboxStates) {
      try {
        const loadedCheckboxStates = typeof tour.checkboxStates === 'string' 
          ? JSON.parse(tour.checkboxStates) 
          : tour.checkboxStates;
        console.log('🔄 Reloading checkbox states from tour update:', loadedCheckboxStates);
        setCheckboxState({
          hotels: loadedCheckboxStates.hotels || [false, false, false, false],
          voucher: loadedCheckboxStates.voucher || [false, false, false, false],
          trainTickets: loadedCheckboxStates.trainTickets || [false, false, false, false],
          airTicket: loadedCheckboxStates.airTicket || [false, false, false, false],
          trainTkt: loadedCheckboxStates.trainTkt || [false, false, false, false],
          safaris: loadedCheckboxStates.safaris || [false, false, false, false],
          trainTicket120: loadedCheckboxStates.trainTicket120 || [false, false, false, false],
          specials: loadedCheckboxStates.specials || [false, false, false, false]
        });
      } catch (e) {
        console.error('Failed to reload checkbox states:', e);
      }
    }
  }, [tour]);

  // Auto-generate file code when series fields change
  useEffect(() => {
    const generateFileCode = async () => {
      // Only generate for new tours
      if (!seriesFields || !tour.id?.startsWith('new-')) {
        return;
      }

      // Only generate when all required fields are filled (including fileName)
      if (!seriesFields.fileName || !seriesFields.city || !seriesFields.fileManager || 
          !seriesFields.month || !seriesFields.year || !seriesFields.fileTourOperator) {
        console.log('⏸️ Waiting for all fields (including file name) to be filled before generating file code');
        return;
      }

      const cityCode = CITY_CODES[seriesFields.city] || 'XX';
      const managerCode = FILE_MANAGER_CODES[seriesFields.fileManager] || 'XX';
      const monthCode = String(seriesFields.month).padStart(2, '0');
      const yearCode = String(seriesFields.year).slice(-2);
      const operatorCode = AGENT_CODES[seriesFields.fileTourOperator] || 'XX';
      
      // Generate the file code prefix
      const codePrefix = `${cityCode}${managerCode}${monthCode}${yearCode}${operatorCode}`;
      
      try {
        // Fetch next sequence number from backend (counts ALL tour files globally, not role-filtered)
        const response = await tourFileAPI.getNextSequence(seriesFields.fileName.trim());
        if (response && response.success) {
          const nextNumber = response.data.nextNumber;
          const sequenceNumber = String(nextNumber).padStart(4, '0');
          
          const generatedFileCode = `${codePrefix}${sequenceNumber}`;
          
          console.log('🔢 File code generation:', {
            seriesName: seriesFields.fileName,
            prefix: codePrefix,
            totalCount: response.data.count,
            nextSequence: nextNumber,
            generatedCode: generatedFileCode
          });
          
          setEditedTour(prev => ({
            ...prev,
            fileCode: generatedFileCode
          }));
        }
      } catch (error) {
        console.error('Failed to fetch next sequence number:', error);
        // Fallback to 0001 if fetch fails
        const generatedFileCode = `${codePrefix}0001`;
        setEditedTour(prev => ({
          ...prev,
          fileCode: generatedFileCode
        }));
      }
    };
    
    generateFileCode();
  }, [seriesFields?.fileName, seriesFields?.city, seriesFields?.fileManager, seriesFields?.month, seriesFields?.year, seriesFields?.fileTourOperator, tour.id]);

  // Load series templates from database
  const [seriesTemplates, setSeriesTemplates] = useState<{[key: string]: any}>({});
  
  useEffect(() => {
    const loadSeriesTemplates = async () => {
      try {
        const result = await seriesTemplateAPI.getAll();
        
        if (result.success && result.data) {
          const templates: {[key: string]: any} = {};
          result.data.forEach((template: any) => {
            // Parse itineraryTemplate if it's a string
            let itinerary = template.itineraryTemplate;
            if (typeof itinerary === 'string') {
              try {
                itinerary = JSON.parse(itinerary);
              } catch (e) {
                console.error('Failed to parse itinerary for', template.seriesName, e);
                itinerary = [];
              }
            }
            templates[template.seriesName.trim()] = { itinerary, defaultProviders: template.defaultProviders || [] };
            console.log(`📋 Template "${template.seriesName.trim()}":`, itinerary?.length || 0, 'rows, providers:', (template.defaultProviders || []).length);
          });
          setSeriesTemplates(templates);
          console.log('📚 Loaded series templates from database:', Object.keys(templates));
        }
      } catch (error) {
        console.error('❌ Failed to load series templates:', error);
      }
    };
    
    loadSeriesTemplates();
  }, []);

  // Auto-fill itinerary when file name matches a template
  useEffect(() => {
    if (seriesFields?.fileName && tour.id?.startsWith('new-')) {
      const fileName = seriesFields.fileName.trim();
      console.log('🔍 Looking for template:', fileName);
      console.log('📚 Available templates:', Object.keys(seriesTemplates));
      console.log('🔎 Full templates object:', seriesTemplates);
      
      const templateData = seriesTemplates[fileName];
      
      console.log('📦 Retrieved template for', fileName, ':', templateData);

      // Load series-specific providers for transport dropdown
      if (templateData && templateData.defaultProviders && templateData.defaultProviders.length > 0) {
        setTransportProviders(templateData.defaultProviders);
      }
      
      const template = templateData?.itinerary;
      
      if (template && Array.isArray(template) && template.length > 0) {
        console.log('🎯 Auto-filling itinerary for:', fileName, 'with', template.length, 'rows');
        const filledRows = template.map((item: any) => ({
          guideName: '',
          guideType: '',
          date: '', // Don't auto-fill dates
          sector: item.sector || '',
          flightNumber: item.flightNumber || '',
          time: item.time || '',
          status: '',
          city: item.city || '',
          hotelName: item.hotelName || '',
          hotelStatus: '',
          roomCategory: '',
          meals: ['Breakfast'] // Default to Breakfast
        }));
        
        setItineraryRows(filledRows);
        console.log('✅ Itinerary auto-filled with', filledRows.length, 'rows');
      } else {
        console.log('⚠️ No valid template found for:', fileName, 'template is:', template);
      }
    }
  }, [seriesFields?.fileName, tour.id, seriesTemplates]);

  // Load series-specific providers for existing tours (based on tourName)
  useEffect(() => {
    if (!tour.id?.startsWith('new-') && editedTour.tourName && Object.keys(seriesTemplates).length > 0) {
      const templateData = seriesTemplates[editedTour.tourName.trim()];
      if (templateData && templateData.defaultProviders && templateData.defaultProviders.length > 0) {
        setTransportProviders(templateData.defaultProviders);
      }
    }
  }, [editedTour.tourName, seriesTemplates, tour.id]);

  // Auto-fill dates in itinerary when Arrival Date changes
  useEffect(() => {
    if (editedTour.startDate && itineraryRows.length > 0 && tour.id?.startsWith('new-')) {
      const arrivalDate = new Date(editedTour.startDate);
      if (!isNaN(arrivalDate.getTime())) {
        console.log('📅 Auto-filling dates from:', editedTour.startDate);
        const updatedRows = itineraryRows.map((row, index) => {
          const rowDate = new Date(arrivalDate);
          rowDate.setDate(arrivalDate.getDate() + index);
          
          // Format date as dd-mmm-yy (26-Feb-26)
          const day = String(rowDate.getDate()).padStart(2, '0');
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const month = monthNames[rowDate.getMonth()];
          const year = String(rowDate.getFullYear()).slice(-2);
          const formattedDate = `${day}-${month}-${year}`;
          
          return {
            ...row,
            date: formattedDate
          };
        });
        setItineraryRows(updatedRows);
        console.log('✅ Dates auto-filled for', updatedRows.length, 'rows');
      }
    }
  }, [editedTour.startDate, tour.id]);

  // Auto-calculate end date from last itinerary row
  useEffect(() => {
    if (itineraryRows.length > 0 && tour.id?.startsWith('new-')) {
      // Find the last row with a date
      const lastRowWithDate = [...itineraryRows].reverse().find(row => row.date && row.date.trim());
      if (lastRowWithDate && lastRowWithDate.date) {
        // Parse dd-mmm-yy format to YYYY-MM-DD
        const parts = lastRowWithDate.date.trim().split('-');
        if (parts.length === 3) {
          const day = parts[0];
          const monthStr = parts[1];
          const year = '20' + parts[2];
          
          const monthMap: {[key: string]: string} = {
            'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
            'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
            'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
          };
          
          const month = monthMap[monthStr];
          if (month) {
            const calculatedEndDate = `${year}-${month}-${day.padStart(2, '0')}`;
            if (calculatedEndDate !== editedTour.endDate) {
              setEditedTour(prev => ({ ...prev, endDate: calculatedEndDate }));
              console.log('📅 Auto-calculated end date from itinerary:', calculatedEndDate);
            }
          }
        }
      }
    }
  }, [itineraryRows, tour.id]);

  // Auto-save to PostgreSQL database when editedTour or checklistItems changes
  useEffect(() => {
    const saveToDatabase = async () => {
      if (tour.id && !tour.id.startsWith('new-') && isEditing && dataLoadedRef.current) {
        try {
          // Extract hotels from checklistItems separately
          const { hotels: hotelsText, ...otherChecklistItems } = checklistItems;
          
          // Extract hotel names from itinerary for storage
          const hotelNamesFromItinerary = itineraryRows
            .map(row => row.hotelName)
            .filter(name => name && name.trim())
            .filter((name, index, self) => self.indexOf(name) === index); // Remove duplicates
          
          const checkboxJSON = JSON.stringify(checkboxState);
          const saveData = {
            ...editedTour,
            // Convert dates to ISO strings to avoid serialization issues
            startDate: editedTour.startDate instanceof Date ? editedTour.startDate.toISOString() : editedTour.startDate,
            endDate: editedTour.endDate instanceof Date ? editedTour.endDate.toISOString() : editedTour.endDate,
            bookingDate: editedTour.bookingDate instanceof Date ? editedTour.bookingDate.toISOString() : editedTour.bookingDate,
            arrivalDate: editedTour.arrivalDate instanceof Date ? editedTour.arrivalDate.toISOString() : editedTour.arrivalDate,
            ...otherChecklistItems,
            // Use hotels from itinerary, not from checklist
            hotels: hotelNamesFromItinerary,
            itinerary: itineraryRows.filter(row => 
              row.date || row.sector || row.flightNumber || row.time || 
              row.status || row.city || row.hotelName || row.hotelStatus || row.roomCategory
            ),
            // Save extensions
            extensions: JSON.stringify(extensions),
            // Save checkbox states
            checkboxStates: checkboxJSON,
            // Save tour leaders and transports
            tourLeaders: JSON.stringify(tourLeaders),
            transports: JSON.stringify(transports),
            // Save passenger details from prop
            passengerDetails: JSON.stringify(passengerDetails)
          };
          
          console.log('💾 Auto-save checkbox states:', checkboxState);
          console.log('💾 Checkbox JSON string:', checkboxJSON);
          console.log('🏨 Saving hotels from itinerary:', hotelNamesFromItinerary);
          await tourFileAPI.update(tour.id, saveData);
          console.log('✅ Auto-saved tour to database:', tour.id);
        } catch (error) {
          console.error('❌ Auto-save failed:', error);
        }
      }
    };

    // Debounce the save to avoid too many API calls
    const timeoutId = setTimeout(saveToDatabase, 1000);
    return () => clearTimeout(timeoutId);
  }, [editedTour, checklistItems, itineraryRows, checkboxState, extensions, tour.id, isEditing, tourLeaders, transports, passengerDetails]);

  const handleSave = async () => {
    try {
      // Prepare data - extract hotels from checklistItems separately
      const { hotels: hotelsText, ...otherChecklistItems } = checklistItems;
      
      // Extract hotel names from itinerary for storage
      const hotelNamesFromItinerary = itineraryRows
        .map(row => row.hotelName)
        .filter(name => name && name.trim())
        .filter((name, index, self) => self.indexOf(name) === index); // Remove duplicates
      
      const checkboxJSON = JSON.stringify(checkboxState);
      const tourLeadersJSON = JSON.stringify(tourLeaders);
      const guidesJSON = JSON.stringify(guides);
      const transportsJSON = JSON.stringify(transports);
      
      const saveData: any = {
        ...editedTour,
        // Convert dates to ISO strings to avoid serialization issues
        startDate: editedTour.startDate instanceof Date ? editedTour.startDate.toISOString() : editedTour.startDate,
        endDate: editedTour.endDate instanceof Date ? editedTour.endDate.toISOString() : editedTour.endDate,
        bookingDate: editedTour.bookingDate instanceof Date ? editedTour.bookingDate.toISOString() : editedTour.bookingDate,
        arrivalDate: editedTour.arrivalDate instanceof Date ? editedTour.arrivalDate.toISOString() : editedTour.arrivalDate,
        ...otherChecklistItems,
        transportType: checklistItems.transport, // Sync to transportType for dashboard
        fileCode: editedTour.fileCode, // Ensure file code is included
        // Use hotels from itinerary, not from checklist
        hotels: hotelNamesFromItinerary,
        itinerary: itineraryRows.filter(row => 
          row.date || row.sector || row.flightNumber || row.time || 
          row.status || row.city || row.hotelName || row.hotelStatus || row.roomCategory
        ),
        // Save extensions
        extensions: JSON.stringify(extensions),
        // Save checkbox states
        checkboxStates: checkboxJSON,
        // Save tour leaders, guides, and transports
        tourLeaders: tourLeadersJSON,
        guides: guidesJSON,
        transports: transportsJSON,
        // Save passenger details from prop
        passengerDetails: JSON.stringify(passengerDetails)
      };
      
      console.log('💾 HandleSave checkbox states:', checkboxState);
      console.log('💾 HandleSave checkbox JSON:', checkboxJSON);
      console.log('💾 HandleSave guides:', guidesJSON);
      console.log('💾 HandleSave tour leaders:', tourLeadersJSON);
      console.log('💾 HandleSave transports:', transportsJSON);
      console.log('📝 HandleSave fileCode:', editedTour.fileCode);

      // If this is a Series tour, include series fields
      if (seriesFields) {
        saveData.tourType = 'SERIES';
        saveData.tourName = seriesFields.fileName.trim(); // Use File Name as tour name
        saveData.clientCountry = seriesFields.country;
        saveData.cities = seriesFields.city ? [seriesFields.city] : [];
        saveData.agentName = seriesFields.fileManager;
        saveData.year = seriesFields.year;
        saveData.month = seriesFields.month;
        saveData.foreignTourOperator = seriesFields.fileTourOperator;
        // File code is already set in editedTour.fileCode from auto-generation
        // Don't send fileCodeBase as it will cause backend to regenerate
      } else {
        saveData.tourType = editedTour.tourType || 'TAILOR_MADE';
      }

      console.log('📤 Saving tour file:', {
        isNew: tour.id.startsWith('new-'),
        tourId: tour.id,
        saveData: saveData
      });

      let response;
      if (tour.id.startsWith('new-')) {
        // Create new tour
        console.log('Creating new tour file via API...');
        response = await tourFileAPI.create(saveData);
        console.log('✅ Tour created response:', response);
      } else {
        // Update existing tour
        console.log('Updating existing tour file via API...');
        response = await tourFileAPI.update(tour.id, saveData);
        console.log('✅ Tour updated response:', response);
      }

      if (response.success && response.data) {
        console.log('✅ Save successful, calling onUpdate with:', response.data);
        if (onUpdate) {
          onUpdate(response.data);
        }
        
        // Check if arrival date is in the past and auto-post warning note
        const tourId = response.data.id || tour.id;
        if (editedTour.startDate) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const arrivalDate = new Date(editedTour.startDate);
          arrivalDate.setHours(0, 0, 0, 0);
          if (arrivalDate < today) {
            try {
              const formattedDate = arrivalDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
              await tourFileAPI.createNote(
                tourId,
                `⚠️ OLD DATE SELECTED: Arrival date (${formattedDate}) is in the past. Please verify if this is correct.`
              );
              console.log('⚠️ Old date warning note posted to conversations');
            } catch (noteError) {
              console.error('Failed to post old date warning note:', noteError);
            }
          }
        }
        
        // Dispatch event to refresh tour list
        window.dispatchEvent(new Event('tourFilesSaved'));
        console.log('✅ Dispatched tourFilesSaved event');
        
        alert('✅ Tour file saved successfully!');
        setIsEditing(false);
        
        // Update local state to reflect the saved tour (with real DB id)
        const parsedHotels = parseArray(response.data.hotels);
        const parsedCities = parseArray(response.data.cities);
        const parsedGuides = parseArray(response.data.guides);
        const parsedItinerary = parseArray(response.data.itinerary);
        
        setEditedTour({
          ...response.data,
          hotels: parsedHotels,
          cities: parsedCities,
          guides: parsedGuides,
          itinerary: parsedItinerary
        });
      } else {
        console.error('❌ Response indicates failure:', response);
        alert('❌ Failed to save tour file');
      }
    } catch (error: any) {
      console.error('❌ Save error:', error);
      alert(`❌ Error saving tour file: ${error.message || 'Unknown error'}`);
    }
  };

  const handleCancel = () => {
    // Reset all states to original tour data
    const parsedHotels = parseArray(tour.hotels);
    const parsedCities = parseArray(tour.cities);
    const parsedGuides = parseArray(tour.guides);
    const parsedItinerary = parseArray(tour.itinerary);
    
    setEditedTour({
      ...tour,
      hotels: parsedHotels,
      cities: parsedCities,
      guides: parsedGuides,
      itinerary: parsedItinerary
    });
    
    setChecklistItems({
      hotels: parsedHotels.join('\n'),
      voucher: tour.voucher || '',
      trainTickets: tour.trainTickets || '',
      airTicket: tour.airTicket || '',
      trainTkt: tour.trainTkt || '',
      safaris: tour.safaris || '',
      trainTicket120: tour.trainTicket120 || '',
      specials: tour.specials || '',
      flight: tour.flight || '',
      flightTime: tour.flightTime || '',
      provider: tour.provider || '',
      transport: tour.transport || ''
    });

    // Reset itinerary
    if (parsedItinerary && parsedItinerary.length > 0) {
      const loadedRows = parsedItinerary.map((row: any) => ({
        guideName: row.guideName || '',
        guideType: row.guideType || '',
        date: row.date || '',
        sector: row.sector || '',
        flightNumber: row.flightNumber || '',
        time: row.time || '',
        status: row.status || '',
        city: row.city || '',
        hotelName: row.hotelName || '',
        hotelStatus: row.hotelStatus || '',
        roomCategory: row.roomCategory || '',
        meals: row.meals || ['Breakfast'] // Default to Breakfast
      }));
      while (loadedRows.length < 3) {
        loadedRows.push({ ...emptyItineraryRow });
      }
      setItineraryRows(loadedRows);
    } else {
      setItineraryRows(Array(3).fill(null).map(() => ({ ...emptyItineraryRow })));
    }
    
    setIsEditing(false);
  };

  // Editable input with better focus and click handling
  const EditableInput = ({ 
    value, 
    onChange, 
    placeholder = '',
    className = ''
  }: { 
    value: string | number, 
    onChange: (val: string) => void, 
    placeholder?: string,
    className?: string
  }) => {
    if (isEditing) {
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full px-2 py-1.5 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 transition-all ${className}`}
          autoComplete="off"
        />
      );
    }
    return <span className="block px-2 py-1.5">{value || <span className="text-gray-400">{placeholder}</span>}</span>;
  };

  const normalizedExtensions = (extensions || []).map((ext) => ({
    ...ext,
    position: ext.position === 'pre' ? 'pre' : 'post'
  }));
  const preExtensions = normalizedExtensions.filter((ext) => ext.position === 'pre');
  const postExtensions = normalizedExtensions.filter((ext) => ext.position === 'post');

  const renderExtensionsSection = (extensionList: typeof normalizedExtensions, title: string) => {
    if (!extensionList || extensionList.length === 0) return null;

    const groupedExtensions: { [name: string]: typeof extensionList } = {};
    extensionList.forEach((ext) => {
      if (!groupedExtensions[ext.passengerName]) {
        groupedExtensions[ext.passengerName] = [];
      }
      groupedExtensions[ext.passengerName].push(ext);
    });

    return (
      <div data-print-extensions className="mt-6">
        <div className="mb-3 px-2 py-1.5 bg-slate-100 border border-slate-300 rounded">
          <h3 className="font-semibold text-xs text-slate-700 uppercase tracking-wide">{title}</h3>
        </div>
        {Object.entries(groupedExtensions).map(([passengerName, rows]) => (
          <div key={`${title}-${passengerName}`} className="mb-6 border-2 border-gray-400">
            <div className="bg-gray-100 px-4 py-2 border-b-2 border-gray-400">
              <h3 className="font-bold text-sm">Extension - {passengerName}</h3>
            </div>

            <div className="grid grid-cols-[0.7fr_0.5fr_0.8fr_0.8fr_0.6fr_0.5fr_0.6fr_0.5fr_0.8fr_1.2fr_0.8fr_0.6fr_0.8fr_1fr] border-b border-gray-400 bg-gray-50">
              <div className="border-r border-gray-400 px-1 py-1 text-center font-medium text-[10px]">Date</div>
              <div className="border-r border-gray-400 px-1 py-1 text-center font-medium text-[10px]">Day</div>
              <div className="border-r border-gray-400 px-1 py-1 text-center font-medium text-[10px]">Sector</div>
              <div className="border-r border-gray-400 px-1 py-1 text-center font-medium text-[10px]">Flight/Train</div>
              <div className="border-r border-gray-400 px-1 py-1 text-center font-medium text-[10px]">Time</div>
              <div className="border-r border-gray-400 px-1 py-1 text-center font-medium text-[10px]">Status</div>
              <div className="border-r border-gray-400 px-1 py-1 text-center font-medium text-[10px]">Guide</div>
              <div className="border-r border-gray-400 px-1 py-1 text-center font-medium text-[10px]">Type</div>
              <div className="border-r border-gray-400 px-1 py-1 text-center font-medium text-[10px]">City</div>
              <div className="border-r border-gray-400 px-1 py-1 text-center font-medium text-[10px]">Hotel</div>
              <div className="border-r border-gray-400 px-1 py-1 text-center font-medium text-[10px]">Status</div>
              <div className="border-r border-gray-400 px-1 py-1 text-center font-medium text-[10px]">Room</div>
              <div className="border-r border-gray-400 px-1 py-1 text-center font-medium text-[10px]">Meals</div>
              <div className="px-1 py-1 text-center font-medium text-[10px]">Remarks</div>
            </div>

            {rows.map((row, rowIdx) => {
              let dayName = '';
              if (row.date) {
                try {
                  const parts = row.date.split('-');
                  if (parts.length === 3) {
                    const d = parseInt(parts[0]);
                    const monthMap: {[k:string]: number} = {'Jan':0,'Feb':1,'Mar':2,'Apr':3,'May':4,'Jun':5,'Jul':6,'Aug':7,'Sep':8,'Oct':9,'Nov':10,'Dec':11};
                    const m = monthMap[parts[1]];
                    const y = parseInt('20' + parts[2]);
                    if (m !== undefined && !isNaN(d) && !isNaN(y)) {
                      dayName = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(y, m, d).getDay()];
                    }
                  }
                } catch {}
              }

              const mealLetters = (row.meals || []).map((m) => m[0]).join('/');
              const remarks = [
                row.breakfastRemark ? `B: ${row.breakfastRemark}` : '',
                row.lunchRemark ? `L: ${row.lunchRemark}` : '',
                row.dinnerRemark ? `D: ${row.dinnerRemark}` : ''
              ].filter(Boolean).join(', ');

              return (
                <div key={rowIdx} className="grid grid-cols-[0.7fr_0.5fr_0.8fr_0.8fr_0.6fr_0.5fr_0.6fr_0.5fr_0.8fr_1.2fr_0.8fr_0.6fr_0.8fr_1fr] min-h-[28px] border-b border-gray-200 last:border-b-0">
                  <div className="border-r border-gray-300 px-1 py-1 text-[10px]">{row.date}</div>
                  <div className="border-r border-gray-300 px-1 py-1 text-[10px] text-center font-medium">{dayName}</div>
                  <div className="border-r border-gray-300 px-1 py-1 text-[10px]">{row.sector}</div>
                  <div className="border-r border-gray-300 px-1 py-1 text-[10px]">{row.flightNumber}</div>
                  <div className="border-r border-gray-300 px-1 py-1 text-[10px]">{row.time}</div>
                  <div className="border-r border-gray-300 px-1 py-1 text-[10px]">{row.status}</div>
                  <div className="border-r border-gray-300 px-1 py-1 text-[10px]">{row.guideName || ''}</div>
                  <div className="border-r border-gray-300 px-1 py-1 text-[10px]">{row.guideType || ''}</div>
                  <div className="border-r border-gray-300 px-1 py-1 text-[10px]">{row.city}</div>
                  <div className="border-r border-gray-300 px-1 py-1 text-[10px]">{row.hotelName}</div>
                  <div className="border-r border-gray-300 px-1 py-1 text-[10px]">{row.hotelStatus}</div>
                  <div className="border-r border-gray-300 px-1 py-1 text-[10px]">{row.roomCategory}</div>
                  <div className="border-r border-gray-300 px-1 py-1 text-[10px] font-medium">{mealLetters}</div>
                  <div className="px-1 py-1 text-[10px] text-gray-600">{remarks}</div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div data-print-tour-cover className="w-full max-w-[1200px] mx-auto bg-white">
      {/* Action Buttons */}
      {canEdit && (
        <div className="flex justify-end gap-2 mb-4">
          {!isEditing ? (
            <Button onClick={() => setIsEditing(true)} variant="outline" size="sm">
              Edit Cover
            </Button>
          ) : (
            <>
              <Button onClick={handleSave} size="sm" className="bg-green-600 hover:bg-green-700 text-white">
                Save Changes
              </Button>
              <Button onClick={handleCancel} variant="outline" size="sm">
                Cancel
              </Button>
            </>
          )}
        </div>
      )}

      {/* Tour File Checklist - Table Format */}
      <div className="border-4 border-black">
        {/* Header Section */}
        <div className="border-b-4 border-black">
          <div className="grid grid-cols-2 min-h-[80px]">
            <div className="flex items-center justify-center border-r-4 border-black p-4">
              <div className="text-xl font-bold text-orange-600">Immerse India<br/>
                <span className="text-sm">Tours Pvt Ltd</span>
              </div>
            </div>
            <div className="flex items-center justify-center p-4">
              <h1 className="text-xl font-bold text-center">Immerse India Tours Pvt Ltd</h1>
            </div>
          </div>
        </div>

        {/* File Code and Checklist Header */}
        <div className="grid grid-cols-2 border-b-2 border-black">          
          <div className="grid grid-cols-5 border-r-2 border-black p-2">
            <span className="col-span-1 font-semibold px-1 py-2">File Code: </span>
            <div className="col-span-2 px-2 py-1">
              <input type="text" value={editedTour.fileCode || ''} onChange={(e) =>isEditing && setEditedTour({ ...editedTour, fileCode: e.target.value })}
              readOnly={!isEditing} className={`border px-2 py-1 w-full ${ !isEditing ? "bg-white cursor-default" : "bg-gray-100" }`}/>
            </div>
          </div>
          <div className="p-3">
            <span className="font-semibold">File Check List -45 days before</span>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-2">
          {/* Left Side - File Details */}
          <div className="border-r-2 border-black">
            {/* File Name */}
            <div className="grid grid-cols-6 border-b border-gray-400 min-h-[40px]">
              <div className="col-span-2 border-r border-gray-400 px-3 py-2 bg-gray-50 font-medium">File Name</div>
              <div className="col-span-4 px-2 py-1">
                {isEditing && (
                  <input 
                    type="text" 
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm" 
                    value={editedTour.tourName} onChange={(e) => setEditedTour({...editedTour, tourName: e.target.value })} />
                )}
                {!isEditing && (
                  <span>{editedTour.tourName}</span>
                )}
              </div>
            </div>

            {/* Client Name - Only show for Tailor Made tours */}
            {tour.tourType !== 'SERIES' && (
              <div className="grid grid-cols-6 border-b border-gray-400 min-h-[40px]">
                <div className="col-span-2 border-r border-gray-400 px-3 py-2 bg-gray-50 font-medium">Client Name</div>
                <div className="col-span-4 px-2 py-1">
                  {isEditing ? (
                    <input 
                      type="text" 
                      className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm" 
                      value={editedTour.clientName || ''} 
                      onChange={(e) => setEditedTour({...editedTour, clientName: e.target.value})} 
                    />
                  ) : (
                    <span className="block px-2 py-1.5">{editedTour.clientName || ''}</span>
                  )}
                </div>
              </div>
            )}

            {/* Arrival Date & Flight */}
            <div className="grid grid-cols-6 border-b border-gray-400 min-h-[40px]">
              <div className="col-span-2 border-r border-gray-400 px-3 py-2 bg-gray-50 font-medium">Arrival Date</div>
              <div className="col-span-4 px-2 py-1">
                {isEditing ? (
                  <input 
                    type="date" 
                    value={editedTour.startDate} 
                    onChange={(e) => setEditedTour({...editedTour, startDate: e.target.value})} 
                    className="w-full px-2 py-1.5 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200" 
                  />
                ) : (
                  <span className="block px-2 py-1.5">{new Date(tour.startDate).toLocaleDateString('en-GB')}</span>
                )}
              </div>
            </div>

            {/* Booking recd on & Flight Time */}
            <div className="grid grid-cols-6 border-b border-gray-400 min-h-[40px]">
              <div className="col-span-2 border-r border-gray-400 px-3 py-2 bg-gray-50 font-medium">Booking recd on</div>
              <div className="col-span-4 px-2 py-1">
                {isEditing ? (
                  <input 
                    type="date" 
                    value={editedTour.bookingDate || ''} 
                    onChange={(e) => setEditedTour({...editedTour, bookingDate: e.target.value})} 
                    className="w-full px-2 py-1.5 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200" 
                  />
                ) : (
                  <span className="block px-2 py-1.5">{editedTour.bookingDate ? new Date(editedTour.bookingDate).toLocaleDateString('en-GB') : ''}</span>
                )}
              </div>
            </div>

            {/* FTO */}
            <div className="grid grid-cols-6 border-b border-gray-400 min-h-[40px]">
              <div className="col-span-2 border-r border-gray-400 px-3 py-2 bg-gray-50 font-medium">FTO</div>
              <div className="col-span-4 px-2 py-1">
                {isEditing ? (
                  <select 
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm" 
                    value={editedTour.foreignTourOperator || ''} 
                    onChange={(e) => setEditedTour({...editedTour, foreignTourOperator: e.target.value})}
                  >
                    <option value="">Select operator...</option>
                    {(() => {
                      const allOperators = Object.keys(AGENT_CODES);
                      const ops = (currentUser && !currentUser.isAdmin && currentUser.assignedOperators && currentUser.assignedOperators.length > 0)
                        ? allOperators.filter(op => currentUser.assignedOperators!.includes(op))
                        : allOperators;
                      return ops.map(op => <option key={op} value={op}>{op}</option>);
                    })()}
                  </select>
                ) : (
                  <span className="block px-2 py-1.5">{editedTour.foreignTourOperator || ''}</span>
                )}
              </div>
            </div>

            {/* Accommodation */}
            <div className="grid grid-cols-6 min-h-[40px]">
                <div className="col-span-2 px-2 py-2 border-r border-gray-400 bg-gray-50 font-medium">Accommodation</div>
                <div className="col-span-1 px-2 py-2 border-r border-gray-400 text-center text-sm bg-gray-50">Single</div>
                <div className="col-span-1 px-2 py-2 border-r border-gray-400 text-center text-sm bg-gray-50">Double</div>
                <div className="col-span-1 px-2 py-2 border-r border-gray-400 text-center text-sm bg-gray-50">Twin</div>
                <div className="col-span-1 px-2 py-2 text-center text-sm bg-gray-50">Triple</div>
            </div>
            <div className="grid grid-cols-6 border border-gray-400 min-h-[40px]">
              <div className="col-span-2 border-r border-gray-400"></div>
              <div className="col-span-1 border-r border-gray-400 px-2 py-1">
                {isEditing ? (
                  <input 
                    type="text" 
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm" 
                    value={editedTour.accomSingle || ''} 
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^[0-9]+$/.test(v)) {
                        const single = parseInt(v) || 0;
                        const double = parseInt(editedTour.accomDouble || '0') || 0;
                        const twin = parseInt(editedTour.accomTwin || '0') || 0;
                        const triple = parseInt(editedTour.accomTriple || '0') || 0;
                        const newPax = (single * 1) + (double * 2) + (twin * 2) + (triple * 3);
                        setEditedTour({...editedTour, accomSingle: v, pax: newPax || 1});
                        if (onPaxChange) onPaxChange(newPax || 1);
                      }
                    }}
                  />
                ) : (
                  <span className="block px-2 py-1.5">{editedTour.accomSingle || ''}</span>
                )}
              </div>
              <div className="col-span-1 border-r border-gray-400 px-2 py-1">
                {isEditing ? (
                  <input 
                    type="text" 
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm" 
                    value={editedTour.accomDouble || ''} 
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^[0-9]+$/.test(v)) {
                        const single = parseInt(editedTour.accomSingle || '0') || 0;
                        const double = parseInt(v) || 0;
                        const twin = parseInt(editedTour.accomTwin || '0') || 0;
                        const triple = parseInt(editedTour.accomTriple || '0') || 0;
                        const newPax = (single * 1) + (double * 2) + (twin * 2) + (triple * 3);
                        setEditedTour({...editedTour, accomDouble: v, pax: newPax || 1});
                        if (onPaxChange) onPaxChange(newPax || 1);
                      }
                    }}
                  />
                ) : (
                  <span className="block px-2 py-1.5">{editedTour.accomDouble || ''}</span>
                )}
              </div>
              <div className="col-span-1 border-r border-gray-400 px-2 py-1">
                {isEditing ? (
                  <input 
                    type="text" 
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm" 
                    value={editedTour.accomTwin || ''} 
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^[0-9]+$/.test(v)) {
                        const single = parseInt(editedTour.accomSingle || '0') || 0;
                        const double = parseInt(editedTour.accomDouble || '0') || 0;
                        const twin = parseInt(v) || 0;
                        const triple = parseInt(editedTour.accomTriple || '0') || 0;
                        const newPax = (single * 1) + (double * 2) + (twin * 2) + (triple * 3);
                        setEditedTour({...editedTour, accomTwin: v, pax: newPax || 1});
                        if (onPaxChange) onPaxChange(newPax || 1);
                      }
                    }}
                  />
                ) : (
                  <span className="block px-2 py-1.5">{editedTour.accomTwin || ''}</span>
                )}
              </div>
              <div className="col-span-1 px-2 py-1">
                {isEditing ? (
                  <input 
                    type="text" 
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm" 
                    value={editedTour.accomTriple || ''} 
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^[0-9]+$/.test(v)) {
                        const single = parseInt(editedTour.accomSingle || '0') || 0;
                        const double = parseInt(editedTour.accomDouble || '0') || 0;
                        const twin = parseInt(editedTour.accomTwin || '0') || 0;
                        const triple = parseInt(v) || 0;
                        const newPax = (single * 1) + (double * 2) + (twin * 2) + (triple * 3);
                        setEditedTour({...editedTour, accomTriple: v, pax: newPax || 1});
                        if (onPaxChange) onPaxChange(newPax || 1);
                      }
                    }}
                  />
                ) : (
                  <span className="block px-2 py-1.5">{editedTour.accomTriple || ''}</span>
                )}
              </div>
            </div>

            {/* Empty spacer to keep left and right grids aligned after moving trip details */}
            <div className="border-t border-gray-300 min-h-[200px]" />
          </div>

          {/* Right Side - Key Trip Details */}
          <div>
            <div className="border-b-2 border-black min-h-[50px] px-3 py-3 bg-gray-100">
              <div className="font-semibold text-center">Trip Summary</div>
            </div>

            <div className="grid grid-cols-5 border-b border-gray-400 min-h-[40px]">
              <div className="col-span-2 border-r border-gray-400 px-3 py-2 bg-gray-50 font-medium">Total Pax</div>
              <div className="col-span-3 px-2 py-1">
                <span className="block px-2 py-1.5 font-medium">{editedTour.pax || 1}</span>
              </div>
            </div>

            <div className="grid grid-cols-5 border-b border-gray-400 min-h-[40px]">
              <div className="col-span-2 border-r border-gray-400 px-3 py-2 bg-gray-50 font-medium">Flight</div>
              <div className="col-span-3 px-2 py-1">
                {isEditing ? (
                  <input
                    type="text"
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm"
                    value={checklistItems.flight}
                    onChange={(e) => setChecklistItems({ ...checklistItems, flight: e.target.value })}
                  />
                ) : (
                  <span className="block px-2 py-1.5">{checklistItems.flight}</span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-5 border-b border-gray-400 min-h-[40px]">
              <div className="col-span-2 border-r border-gray-400 px-3 py-2 bg-gray-50 font-medium">Flight Time</div>
              <div className="col-span-3 px-2 py-1">
                {isEditing ? (
                  <input
                    type="text"
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm"
                    value={checklistItems.flightTime}
                    onChange={(e) => setChecklistItems({ ...checklistItems, flightTime: e.target.value })}
                  />
                ) : (
                  <span className="block px-2 py-1.5">{checklistItems.flightTime}</span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-5 border-b border-gray-400 min-h-[40px]">
              <div className="col-span-2 border-r border-gray-400 px-3 py-2 bg-gray-50 font-medium">Transport</div>
              <div className="col-span-3 px-2 py-1">
                {isEditing ? (
                  <div className="space-y-2">
                    {transports.map((transport, idx) => (
                      <div key={transport.id} className="flex items-center gap-2">
                        <select
                          className="w-24 px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none text-xs"
                          value={transport.type}
                          onChange={(e) => {
                            const updated = [...transports];
                            updated[idx].type = e.target.value;
                            setTransports(updated);
                          }}
                        >
                          <option value="">Select...</option>
                          <option value="Large Couch">Large Couch</option>
                          <option value="Mini Couch">Mini Couch</option>
                          <option value="Urbania">Urbania</option>
                          <option value="Sedan">Sedan</option>
                          <option value="Crysta">Crysta</option>
                          <option value="Tempo Traveller">Tempo Traveller</option>
                          <option value="Volvo Bus">Volvo Bus</option>
                        </select>
                        {tour.tourType === 'SERIES' ? (
                          <select
                            className="w-24 px-1.5 py-1 bg-white border-2 border-blue-400 rounded-md text-xs"
                            value={transport.provider || ''}
                            onChange={(e) => {
                              const updated = [...transports];
                              updated[idx].provider = e.target.value;
                              setTransports(updated);
                            }}
                          >
                            <option value="">Provider</option>
                            {(transportProviders.length > 0 ? transportProviders : allTransportProviders).map((providerName, providerIdx) => (
                              <option key={providerIdx} value={providerName}>{providerName}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            placeholder="Provider"
                            className="w-24 px-1.5 py-1 bg-white border-2 border-blue-400 rounded-md text-xs"
                            value={transport.provider || ''}
                            onChange={(e) => {
                              const updated = [...transports];
                              updated[idx].provider = e.target.value;
                              setTransports(updated);
                            }}
                          />
                        )}
                        <input
                          type="text"
                          placeholder="From"
                          className="w-16 px-1.5 py-1 bg-white border-2 border-blue-400 rounded-md text-xs"
                          value={transport.startDate}
                          onChange={(e) => {
                            const updated = [...transports];
                            updated[idx].startDate = e.target.value;
                            setTransports(updated);
                          }}
                        />
                        <input
                          type="text"
                          placeholder="To"
                          className="w-16 px-1.5 py-1 bg-white border-2 border-blue-400 rounded-md text-xs"
                          value={transport.endDate}
                          onChange={(e) => {
                            const updated = [...transports];
                            updated[idx].endDate = e.target.value;
                            setTransports(updated);
                          }}
                        />
                        <button
                          onClick={() => setTransports(transports.filter((_, i) => i !== idx))}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => setTransports([...transports, { id: Date.now().toString(), type: '', startDate: '', endDate: '', provider: '' }])}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      + Add Transport
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {transports.map((transport) => (
                      <div key={transport.id} className="text-sm">
                        {transport.type}{transport.provider ? ` - ${transport.provider}` : ''} ({transport.startDate} - {transport.endDate})
                      </div>
                    ))}
                    {transports.length === 0 && <span className="text-sm text-gray-500">-</span>}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-5 border-b border-gray-400 min-h-[40px]">
              <div className="col-span-2 border-r border-gray-400 px-3 py-2 bg-gray-50 font-medium">Tour Leader</div>
              <div className="col-span-3 px-2 py-1">
                {isEditing ? (
                  <div className="space-y-2">
                    {tourLeaders.map((leader, idx) => (
                      <div key={leader.id} className="flex items-center gap-2">
                        <select
                          className="flex-1 px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none text-sm"
                          value={leader.name}
                          onChange={(e) => {
                            const updated = [...tourLeaders];
                            updated[idx].name = e.target.value;
                            setTourLeaders(updated);
                          }}
                        >
                          <option value="">Select Tour Leader</option>
                          {availableTourLeaders.map((tourLeader) => (
                            <option key={tourLeader.id} value={tourLeader.name}>{tourLeader.name}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => setTourLeaders(tourLeaders.filter((_, i) => i !== idx))}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                    {tourLeaders.length < 2 && (
                      <button
                        onClick={() => setTourLeaders([...tourLeaders, { id: Date.now().toString(), name: '', type: 'Full Day' }])}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        + Add Tour Leader
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {tourLeaders.map((leader) => (
                      <div key={leader.id} className="text-sm">{leader.name}</div>
                    ))}
                    {tourLeaders.length === 0 && <span className="text-sm text-gray-500">-</span>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {renderExtensionsSection(preExtensions, 'Pre Extensions')}

        {/* Bottom Section - Itinerary Table */}
        <div className="border-t-2 border-black">
          <div className="grid grid-cols-[0.8fr_0.6fr_1fr_3fr_1.5fr_1.3fr_3.5fr_1fr_1.2fr] border-b border-gray-400 bg-gray-100">
            <div className="border-r border-gray-400 px-2 py-2 text-center font-medium text-sm">Date</div>
            <div className="border-r border-gray-400 px-2 py-2 text-center font-medium text-sm">Day</div>
            <div className="border-r border-gray-400 px-2 py-2 text-center font-medium text-sm">Sector</div>
            <div className="border-r border-gray-400 px-2 py-2 text-center font-medium text-sm">Flights / Train</div>
            <div className="border-r border-gray-400 px-2 py-2 text-center font-medium text-sm">Guide</div>
            <div className="border-r border-gray-400 px-2 py-2 text-center font-medium text-sm">City</div>
            <div className="border-r border-gray-400 px-2 py-2 text-center font-medium text-sm">Hotel Reservation</div>
            <div className="border-r border-gray-400 px-2 py-2 text-center font-medium text-sm">Room Category</div>
            <div className="px-2 py-2 text-center font-medium text-sm">Meals</div>
          </div>

          <div className="grid grid-cols-[0.8fr_0.6fr_1fr_1fr_1fr_1fr_1fr_0.5fr_1.3fr_2.7fr_0.8fr_1fr_1.2fr] border-b border-gray-400 bg-gray-50 [&>div]:min-w-0 [&>div]:overflow-hidden">
            <div className="col-span-3 border-r border-gray-400"></div>
            <div className="border-r border-gray-400 px-2 py-1 text-center text-xs">Flight / Train number</div>
            <div className="border-r border-gray-400 px-2 py-1 text-center text-xs">Time</div>
            <div className="border-r border-gray-400 px-2 py-1 text-center text-xs">Status</div>
            <div className="border-r border-gray-400 px-2 py-1 text-center text-xs">Name</div>
            <div className="border-r border-gray-400 px-2 py-1 text-center text-xs">Type</div>
            <div className="border-r border-gray-400"></div>
            <div className="border-r border-gray-400 px-2 py-1 text-center text-xs">Hotel Name</div>
            <div className="border-r border-gray-400 px-1 py-1 text-center text-xs">Status</div>
            <div className="border-r border-gray-400"></div>
            <div className="px-1 py-1 text-center text-xs">B/L/D</div>
          </div>

          {/* Itinerary rows - initially 3 rows */}
          {itineraryRows.map((row, rowIdx) => (
            <div key={rowIdx} className="grid grid-cols-[0.8fr_0.6fr_1fr_1fr_1fr_1fr_1fr_0.5fr_1.3fr_2.7fr_0.8fr_1fr_1.2fr] border-b border-gray-400 min-h-[45px] [&>div]:min-w-0 [&>div]:overflow-hidden">
              {/* Date */}
              <div className="border-r border-gray-400 px-2 py-2">
                {isEditing ? (
                  <input 
                    type="text" 
                    value={row.date}
                    onChange={(e) => {
                      const newRows = [...itineraryRows];
                      newRows[rowIdx].date = e.target.value;
                      setItineraryRows(newRows);
                    }}
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm" 
                  />
                ) : (
                  <span className="block px-2 py-1 text-sm">{row.date}</span>
                )}
              </div>
              
              {/* Day */}
              <div className="border-r border-gray-400 px-2 py-2 text-center">
                <span className="block px-1 py-1 text-sm font-medium">
                  {getDayOfWeek(row.date) || `Day ${rowIdx + 1}`}
                </span>
              </div>
              
              {/* Sector */}
              <div className="border-r border-gray-400 px-2 py-2">
                {isEditing ? (
                  <input 
                    type="text" 
                    value={row.sector}
                    onChange={(e) => {
                      const newRows = [...itineraryRows];
                      newRows[rowIdx].sector = e.target.value;
                      setItineraryRows(newRows);
                    }}
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm" 
                  />
                ) : (
                  <span className="block px-2 py-1 text-sm">{row.sector}</span>
                )}
              </div>
              
              {/* Flight/Train Number */}
              <div className="border-r border-gray-400 px-2 py-2">
                {isEditing ? (
                  <input 
                    type="text" 
                    value={row.flightNumber}
                    onChange={(e) => {
                      const newRows = [...itineraryRows];
                      newRows[rowIdx].flightNumber = e.target.value;
                      setItineraryRows(newRows);
                    }}
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm" 
                  />
                ) : (
                  <span className="block px-2 py-1 text-sm">{row.flightNumber}</span>
                )}
              </div>
              
              {/* Time */}
              <div className="border-r border-gray-400 px-2 py-2">
                {isEditing ? (
                  <input 
                    type="text" 
                    value={row.time}
                    onChange={(e) => {
                      const newRows = [...itineraryRows];
                      newRows[rowIdx].time = e.target.value;
                      setItineraryRows(newRows);
                    }}
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm" 
                  />
                ) : (
                  <span className="block px-2 py-1 text-sm">{row.time}</span>
                )}
              </div>
              
              {/* Status */}
              <div className="border-r border-gray-400 px-2 py-2">
                {isEditing ? (
                  <input 
                    type="text" 
                    value={row.status}
                    onChange={(e) => {
                      const newRows = [...itineraryRows];
                      newRows[rowIdx].status = e.target.value;
                      setItineraryRows(newRows);
                    }}
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm" 
                  />
                ) : (
                  <span className="block px-2 py-1 text-sm">{row.status}</span>
                )}
              </div>
              
              {/* Guide Name */}
              <div className="border-r border-gray-400 px-2 py-2">
                {isEditing ? (
                  <input 
                    type="text" 
                    value={row.guideName}
                    onChange={(e) => {
                      const newRows = [...itineraryRows];
                      newRows[rowIdx].guideName = e.target.value;
                      setItineraryRows(newRows);
                    }}
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-xs" 
                    placeholder="Guide name"
                  />
                ) : (
                  <span className="block px-1 py-1 text-xs truncate" title={row.guideName}>{row.guideName}</span>
                )}
              </div>
              
              {/* Guide Type */}
              <div className="border-r border-gray-400 px-2 py-2">
                {isEditing ? (
                  <select 
                    value={row.guideType}
                    onChange={(e) => {
                      const newRows = [...itineraryRows];
                      newRows[rowIdx].guideType = e.target.value;
                      setItineraryRows(newRows);
                    }}
                    className="w-full px-1 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-xs" 
                  >
                    <option value="">-</option>
                    <option value="F.D">F.D</option>
                    <option value="H.D">H.D</option>
                  </select>
                ) : (
                  <span className="block px-1 py-1 text-xs text-center">{row.guideType}</span>
                )}
              </div>
              
              {/* City */}
              <div className="border-r border-gray-400 px-2 py-2">
                {isEditing ? (
                  <input 
                    type="text" 
                    value={row.city}
                    onChange={(e) => {
                      const newRows = [...itineraryRows];
                      newRows[rowIdx].city = e.target.value;
                      setItineraryRows(newRows);
                    }}
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm" 
                  />
                ) : (
                  <span className="block px-2 py-1 text-sm">{row.city}</span>
                )}
              </div>
              
              {/* Hotel Name */}
              <div className="border-r border-gray-400 px-2 py-2">
                {isEditing ? (
                  <input 
                    type="text" 
                    value={row.hotelName}
                    onChange={(e) => {
                      const newRows = [...itineraryRows];
                      newRows[rowIdx].hotelName = e.target.value;
                      setItineraryRows(newRows);
                    }}
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm" 
                  />
                ) : (
                  <span className="block px-1 py-1 text-xs truncate" title={row.hotelName}>{row.hotelName}</span>
                )}
              </div>
              
              {/* Hotel Status */}
              <div className="border-r border-gray-400 px-2 py-2">
                {isEditing ? (
                  <input 
                    type="text" 
                    value={row.hotelStatus}
                    onChange={(e) => {
                      const newRows = [...itineraryRows];
                      newRows[rowIdx].hotelStatus = e.target.value;
                      setItineraryRows(newRows);
                    }}
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm" 
                  />
                ) : (
                  <span className="block px-2 py-1 text-sm">{row.hotelStatus}</span>
                )}
              </div>
              
              {/* Room Category */}
              <div className="border-r border-gray-400 px-2 py-2">
                {isEditing ? (
                  <input 
                    type="text" 
                    value={row.roomCategory}
                    onChange={(e) => {
                      const newRows = [...itineraryRows];
                      newRows[rowIdx].roomCategory = e.target.value;
                      setItineraryRows(newRows);
                    }}
                    className="w-full px-2 py-1 bg-white border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 text-sm" 
                  />
                ) : (
                  <span className="block px-2 py-1 text-sm">{row.roomCategory}</span>
                )}
              </div>

              {/* Meals Column */}
              <div className="px-1 py-1 flex items-center justify-center">
                {isEditing ? (
                  <div className="flex flex-col gap-0.5 text-xs w-full">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={row.meals?.includes('Breakfast') ?? true}
                        onChange={(e) => {
                          const newRows = [...itineraryRows];
                          let currentMeals = [...(newRows[rowIdx].meals || ['Breakfast'])];
                          if (e.target.checked) {
                            if (!currentMeals.includes('Breakfast')) currentMeals.push('Breakfast');
                          } else {
                            currentMeals = currentMeals.filter(m => m !== 'Breakfast');
                            newRows[rowIdx].breakfastRemark = ''; // Clear remark if unchecked
                          }
                          newRows[rowIdx].meals = currentMeals;
                          setItineraryRows(newRows);
                        }}
                        className="w-3 h-3 cursor-pointer"
                      />
                      <span>B</span>
                    </label>
                    {row.meals?.includes('Breakfast') && (
                      <input
                        type="text"
                        placeholder="Breakfast at..."
                        value={row.breakfastRemark || ''}
                        onChange={(e) => {
                          const newRows = [...itineraryRows];
                          newRows[rowIdx].breakfastRemark = e.target.value;
                          setItineraryRows(newRows);
                        }}
                        className="w-full px-1 py-0.5 text-xs border rounded"
                      />
                    )}
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={row.meals?.includes('Lunch') ?? false}
                        onChange={(e) => {
                          const newRows = [...itineraryRows];
                          let currentMeals = [...(newRows[rowIdx].meals || ['Breakfast'])];
                          if (e.target.checked) {
                            if (!currentMeals.includes('Lunch')) currentMeals.push('Lunch');
                          } else {
                            currentMeals = currentMeals.filter(m => m !== 'Lunch');
                            newRows[rowIdx].lunchRemark = ''; // Clear remark if unchecked
                          }
                          newRows[rowIdx].meals = currentMeals;
                          setItineraryRows(newRows);
                        }}
                        className="w-3 h-3 cursor-pointer"
                      />
                      <span>L</span>
                    </label>
                    {row.meals?.includes('Lunch') && (
                      <input
                        type="text"
                        placeholder="Lunch at..."
                        value={row.lunchRemark || ''}
                        onChange={(e) => {
                          const newRows = [...itineraryRows];
                          newRows[rowIdx].lunchRemark = e.target.value;
                          setItineraryRows(newRows);
                        }}
                        className="w-full px-1 py-0.5 text-xs border rounded"
                      />
                    )}
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={row.meals?.includes('Dinner') ?? false}
                        onChange={(e) => {
                          const newRows = [...itineraryRows];
                          let currentMeals = [...(newRows[rowIdx].meals || ['Breakfast'])];
                          if (e.target.checked) {
                            if (!currentMeals.includes('Dinner')) currentMeals.push('Dinner');
                          } else {
                            currentMeals = currentMeals.filter(m => m !== 'Dinner');
                            newRows[rowIdx].dinnerRemark = ''; // Clear remark if unchecked
                          }
                          newRows[rowIdx].meals = currentMeals;
                          setItineraryRows(newRows);
                        }}
                        className="w-3 h-3 cursor-pointer"
                      />
                      <span>D</span>
                    </label>
                    {row.meals?.includes('Dinner') && (
                      <input
                        type="text"
                        placeholder="Dinner at..."
                        value={row.dinnerRemark || ''}
                        onChange={(e) => {
                          const newRows = [...itineraryRows];
                          newRows[rowIdx].dinnerRemark = e.target.value;
                          setItineraryRows(newRows);
                        }}
                        className="w-full px-1 py-0.5 text-xs border rounded"
                      />
                    )}
                  </div>
                ) : (
                  <div className="text-xs">
                    <div className="font-medium">{(row.meals || ['Breakfast']).map(m => m[0]).join('/')}</div>
                    {row.breakfastRemark && <div className="text-gray-600 mt-0.5">B: {row.breakfastRemark}</div>}
                    {row.lunchRemark && <div className="text-gray-600 mt-0.5">L: {row.lunchRemark}</div>}
                    {row.dinnerRemark && <div className="text-gray-600 mt-0.5">D: {row.dinnerRemark}</div>}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Add Row Button */}
          {isEditing && (
            <div className="border-t-2 border-gray-400 py-3 px-4 bg-gray-50 flex justify-center">
              <button
                onClick={() => {
                  // Find the last row with a valid date to compute next date
                  let nextDate = '';
                  for (let i = itineraryRows.length - 1; i >= 0; i--) {
                    const dateStr = itineraryRows[i].date;
                    if (dateStr && dateStr.trim()) {
                      try {
                        const parts = dateStr.trim().split('-');
                        if (parts.length === 3) {
                          const day = parseInt(parts[0]);
                          const monthMap: {[key: string]: number} = {
                            'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3,
                            'May': 4, 'Jun': 5, 'Jul': 6, 'Aug': 7,
                            'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
                          };
                          const month = monthMap[parts[1]];
                          const year = parseInt('20' + parts[2]);
                          if (month !== undefined && !isNaN(day) && !isNaN(year)) {
                            const lastDate = new Date(year, month, day);
                            lastDate.setDate(lastDate.getDate() + 1);
                            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                            nextDate = `${String(lastDate.getDate()).padStart(2, '0')}-${monthNames[lastDate.getMonth()]}-${String(lastDate.getFullYear()).slice(-2)}`;
                          }
                        }
                      } catch (e) { /* ignore parse errors */ }
                      break;
                    }
                  }
                  setItineraryRows([...itineraryRows, { ...emptyItineraryRow, date: nextDate }]);
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 font-medium flex items-center gap-2"
              >
                <span className="text-xl">+</span> Add Row
              </button>
            </div>
          )}
        </div>
      </div>

      {renderExtensionsSection(postExtensions, 'Post Extensions')}
    </div>
  );
}

